import { FastlyImage } from '@components/common/image'
import { DoubleQuotes } from '@components/common/icons/custom-icons'
import { LightBulbIcon } from '@heroicons/react/24/outline'
import BookFreeClassButton from '@components/common/book-free-class-btn'
import { LandingCard } from '@components/coding-landing/types'

const QuoteCard = ({
  quoteCard,
}: {
  quoteCard: NonNullable<LandingCard['quoteCard']>
}) => (
  <div
    className={`${quoteCard.bg} ${
      quoteCard.isFullWidth
        ? 'col-start-1 col-end-3 max-w-5xl mx-auto mt-3 lg:mt-6'
        : ''
    } rounded-xl p-3 overflow-hidden relative`}
  >
    <div className="flex items-center gap-4">
      {quoteCard.image && (
        <FastlyImage
          src={quoteCard.image}
          alt={quoteCard.imageAlt ?? ''}
          width={71}
          height={74}
          className="rounded-lg object-cover flex-shrink-0 self-start"
        />
      )}
      <div className="relative">
        <p className="text-grey-900 text-sm lg:text-base leading-tight">
          {quoteCard.quote}
        </p>
        {!quoteCard.isFullWidth && (
          <DoubleQuotes className="absolute h-16 w-16 right-0 top-0 mr-5 z-0 opacity-25" />
        )}
        {quoteCard.heading && (
          <p className="font-700 text-grey-900 text-sm mb-2 relative">
            {quoteCard.heading}
          </p>
        )}
        {quoteCard.attribution && (
          <p className="text-sm">
            <span className="text-orange font-700">
              {quoteCard.attribution}
            </span>
            {quoteCard.attributionRole && (
              <span className="text-grey-800">
                , {quoteCard.attributionRole}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  </div>
)

const CalloutCard = ({
  calloutCard,
}: {
  calloutCard: NonNullable<LandingCard['calloutCard']>
}) => (
  <div className="bg-yellow-100 bg-opacity-50 border border-grey-400 rounded-xl p-4 mb-5">
    <div className="flex items-start gap-3">
      <span className="w-2 h-2 rounded-full bg-orange flex-shrink-0 mt-2" />
      <div className="flex-1">
        <p className="font-700 text-grey-900 text-xs uppercase tracking-wider mb-1">
          {calloutCard.eyebrow}
        </p>
        <p className="font-700 text-orange text-base italic mb-2">
          {calloutCard.tagline}
        </p>
        <p className="text-grey-800 text-sm leading-relaxed">
          {calloutCard.description}
        </p>
      </div>
    </div>
  </div>
)

export const BenefitCard = ({
  card,
  anchorPrefix = 'benefit',
}: {
  card: LandingCard
  anchorPrefix?: string
}) => (
  <section
    className={`py-12 lg:py-16 px-5 lg:px-8 bg-opacity-25 ${card.sectionBg}`}
    id={`${anchorPrefix}-${card.number}`}
  >
    <div className="gap-6 container mx-auto">
      {/* Number + icon */}
      <div className="flex items-center gap-4">
        <span className="text-6xl font-700 leading-none select-none text-orange-300 opacity-75">
          {card.number}
        </span>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-orange-200 text-orange">
          <span className="text-3xl p-2">
            <card.icon className="w-6 h-6" />
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 max-w-6xl mx-auto gap-8">
        {/* Left column */}
        <div className="w-full flex flex-col justify-between">
          <div>
            <h2 className="font-500 text-2xl lg:text-3xl leading-snug mb-5">
              {card.eyebrow && (
                <span className="text-orange">{card.eyebrow} </span>
              )}
              {card.heading}
            </h2>

            <div className="text-base lg:text-lg leading-relaxed space-y-3 mb-5">
              {card.bodyParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          {/* Feature bullets */}
          {card.features && card.features.length > 0 && (
            <div className="space-y-2 mb-6">
              {card.features.map((feature, i) => (
                <div
                  key={i}
                  className={`${feature.bgClass} border border-grey-300 rounded-xl p-3 py-4 flex items-start gap-3`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0 mt-2 bg-orange" />
                  <div>
                    <h3 className="flex flex-wrap items-baseline gap-2 mb-1">
                      {feature.label}
                      {feature.sublabel && <span>{feature.sublabel}</span>}
                    </h3>
                    {feature.description}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats card */}
          {card.statsCard && (
            <div className="bg-white rounded-xl ">
              <div className="bg-yellow-100 bg-opacity-50 border border-grey-400 p-4 rounded-xl">
                <div className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-orange flex-shrink-0 mt-2" />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline gap-3 mb-1">
                      <span className="font-700 text-grey-900 text-xs uppercase tracking-wider">
                        {card.statsCard.category}
                      </span>
                      <span className="font-700 text-orange text-base">
                        {card.statsCard.value}
                      </span>
                    </div>
                    <p className="text-grey-800 text-sm leading-relaxed">
                      {card.statsCard.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Callout card */}
          {card.calloutCard && <CalloutCard calloutCard={card.calloutCard} />}

          {card.research && (
            <p className="text-xs italic" style={{ color: '#4486f4' }}>
              🔬 Research Basis: {card.research}
            </p>
          )}
        </div>

        {/* Right column: image + cards */}
        <div className="w-full space-y-3">
          <div className="overflow-hidden">
            <FastlyImage
              src={card.imageSrc}
              alt={card.imageAlt}
              width={card.imageWidth ?? 571}
              height={card.imageHeight ?? 372}
              className="h-full object-scale-down rounded-xl"
            />
          </div>

          {card.whatThisMeans && (
            <div className="bg-white border-2 border-grey-400 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-3">
                <LightBulbIcon className="w-6 h-6 text-orange" />
                <h3 className="font-700 text-grey-900 text-lg">
                  What This Means for Your Child
                </h3>
              </div>
              <p className="text-grey-800 text-base leading-relaxed">
                {card.whatThisMeans}
              </p>
            </div>
          )}

          {card.quoteCard && !card.quoteCard.isFullWidth && (
            <QuoteCard quoteCard={card.quoteCard} />
          )}
        </div>
      </div>

      {/* Full width quote card */}
      {card.quoteCard && card.quoteCard.isFullWidth && (
        <QuoteCard quoteCard={card.quoteCard} />
      )}

      {/* CTA */}
      {card.showCTA && (
        <div className="flex justify-center mt-10 col-span-2">
          <BookFreeClassButton variant="solid" shouldScrollToTop />
        </div>
      )}
    </div>
  </section>
)

export const BenefitsSection = ({
  cards,
  anchorPrefix = 'benefit',
  children,
}: {
  cards: LandingCard[]
  anchorPrefix?: string
  children?: React.ReactNode
}) => (
  <div className="pt-5 lg:pt-8 space-y-12 lg:space-y-16">
    <div>
      {cards.map((card) => (
        <BenefitCard
          key={card.number}
          card={card}
          anchorPrefix={anchorPrefix}
        />
      ))}
    </div>
    {children}
  </div>
)
